import MembershipOfferingModel from "src/models/MembershipOfferingModel";
import OrganizationModel from "src/models/OrganizationModel";
import {
  MembershipOfferingCreateInput,
  createMembershipOfferingVariables,
  updateMembershipOfferingVariables,
} from "types/code-generator";

interface GetGroupOfferingVariablesInput {
  offering: MembershipOfferingModel;
  currentOffering?: MembershipOfferingModel;
  organization: OrganizationModel;
}

export function getMembershipOfferingVariables({
  offering,
  currentOffering,
  organization,
}: GetGroupOfferingVariablesInput):
  | createMembershipOfferingVariables
  | updateMembershipOfferingVariables {
  let department = organization.defaultDepartment;

  if (offering.department) {
    department = offering.department;
  }

  const data: MembershipOfferingCreateInput = {
    agreements: {
      connect: offering.agreements.map((a) => ({ id: a.id })),
    },
    allowCustomerCancellation: offering.allowCustomerCancellation,
    allowSharing: offering.allowSharing,
    autoEnroll: offering.autoEnroll,
    charges: {
      create: offering.charges
        .filter((i) => i.id.includes("-"))
        .map((c) => {
          return {
            amount: c.amount,
            appliesTo: c.appliesTo,
            billingCategory: {
              connect: {
                id: c.billingCategory?.id,
              },
            },
            description: c.description,
          };
        }),
    },
    checkInsPerWeek: offering.checkInsPerWeek,
    checkInsPerMonth: offering.checkInsPerMonth,
    coupons: {
      connect: offering.coupons.map((c) => ({ id: c.id })),
    },
    credits: {
      create: offering.credits
        .filter((i) => i.id.includes("-"))
        .map((c) => {
          return {
            amount: c.amount,
            issuedAmount: c.amount,
            billingCategory: {
              connect: {
                id: c.billingCategory?.id,
              },
            },
            description: c.description,
          };
        }),
    },
    department:
      offering.department === null
        ? undefined
        : {
            connect: {
              id: offering.department?.id || organization.defaultDepartment?.id,
            },
          },
    description: offering.description,
    displayOpenSpots: offering.displayOpenSpots,
    endDate: offering.endDate,
    endDays: offering.endDays,
    fees: {
      connect: offering.fees.map((f) => ({ id: f.id })),
    },
    freePresentation: offering.freePresentation,
    maintainWaitlist: offering.maintainWaitlist,
    maxCheckIns: offering.maxCheckIns,
    maxEnrollment: offering.maxEnrollment,
    maxMembers: offering.maxMembers,
    organization: {
      connect: {
        id: organization.id,
      },
    },
    participantType: offering.participantType,
    passType: offering.passType,
    paymentFrequency: offering.paymentFrequency,
    price: offering.price,
    priceLevels: {
      create: offering.priceLevels
        .filter((i) => i.id.includes("-") && i.price)
        .map((i) => {
          return {
            price: i.price,
            number: i.number,
          };
        }),
    },
    pricePerCheckIn: offering.pricePerCheckIn,
    prorate: offering.prorate,
    prorateStartEnd: offering.prorateStartEnd,
    publishDate: offering.publishDate,
    recurringDate: offering.recurringDate,
    registrationClose: offering.registrationClose,
    registrationOpen: offering.registrationOpen,
    renewalType: offering.renewalType,
    startDate: offering.startDate,
    status: offering.status,
    studentInfoRequests: {
      connect: offering.studentInfoRequests.map((i) => ({ id: i.id })),
    },
    tags: {
      create: offering.tags
        ?.filter((t) => t.id.includes("-"))
        .map((t) => ({
          title: t.title,
          hideInFilters: t.hideInFilters,
          department: department
            ? {
                connect: {
                  id: department?.id,
                },
              }
            : undefined,
        })),
    },
    title: offering.title,
    type: offering.type,
    unpublishDate: offering.unpublishDate,
    waitlistCapacity: offering.waitlistCapacity,
  };

  if (offering.program) {
    if (offering.program.id === "new") {
      data.program = {
        create: {
          title: offering.program.title,
          description: offering.program.description,
          department: {
            connect: {
              id: offering.department
                ? offering.department.id
                : organization.defaultDepartment?.id,
            },
          },
          billingCategory: {
            create: {
              active: true,
              department: {
                connect: {
                  id: offering.department
                    ? offering.department.id
                    : organization.defaultDepartment?.id,
                },
              },
              organization: {
                connect: {
                  id: organization.id,
                },
              },
              excludeFromReports: false,
              title: offering.program.title,
            },
          },
          organization: {
            connect: {
              id: organization.id,
            },
          },
        },
      };
    } else {
      data.program = {
        connect: {
          id: offering.program.id,
        },
      };
    }
  }

  // if (!offering.billingCategory) {
  //   data.billingCategory = {
  //     create: {
  //       active: true,
  //       excludeFromReports: false,
  //       title: offering.title,
  //       organization: {
  //         connect: {
  //           id: organization.id,
  //         },
  //       },
  //     },
  //   };
  // } else {
  //   data.billingCategory = {
  //     connect: {
  //       id: offering.billingCategory.id,
  //     },
  //   };
  // }

  if (offering.id) {
    const updateVariables: updateMembershipOfferingVariables = {
      data: {
        ...data,
        addons: {
          set: offering.addons.map((i) => ({ id: i.id })),
        },
        agreements: {
          set: offering.agreements.map((i) => ({ id: i.id })),
        },
        charges: {
          create: data.charges?.create,
          update: offering.charges
            .filter((i) => !i.id.includes("-"))
            .map((i) => {
              return {
                data: {
                  amount: i.amount,
                  appliesTo: i.appliesTo,
                  billingCategory: {
                    connect: {
                      id: i.billingCategory?.id,
                    },
                  },
                  description: i.description,
                },
                where: {
                  id: i.id,
                },
              };
            }),
          delete: currentOffering?.charges
            .filter((i) => !offering?.charges.find((ci) => ci.id === i.id))
            .map((i) => {
              return {
                id: i.id,
              };
            }),
        },
        communications: {
          set: offering.communications.map((i) => ({ id: i.id })),
        },
        coupons: {
          set: offering.coupons.map((i) => ({ id: i.id })),
        },
        credits: {
          create: data.credits?.create,
          update: offering.credits
            .filter((i) => !i.id.includes("-"))
            .map((i) => {
              return {
                data: {
                  amount: i.amount,
                  issuedAmount: i.amount,
                  billingCategory: {
                    connect: {
                      id: i.billingCategory?.id,
                    },
                  },
                  description: i.description,
                },
                where: {
                  id: i.id,
                },
              };
            }),
          delete: currentOffering?.credits
            .filter((i) => !offering?.credits.find((ci) => ci.id === i.id))
            .map((i) => {
              return {
                id: i.id,
              };
            }),
        },
        department:
          offering.department === null ? { disconnect: true } : data.department,
        fees: {
          set: offering.fees.map((f) => ({ id: f.id })),
        },
        priceLevels: {
          create: data.priceLevels?.create,
          update: offering.priceLevels
            ?.filter((i) => !i.id.includes("-"))
            .map((i) => {
              return {
                data: {
                  number: i.number,
                  price: i.price,
                },
                where: {
                  id: i.id,
                },
              };
            }),
          delete: currentOffering.priceLevels
            ?.filter((i) => !offering?.priceLevels.find((ci) => ci.id === i.id))
            .map((i) => {
              return {
                id: i.id,
              };
            }),
        },
        studentInfoRequests: {
          set: offering.studentInfoRequests.map((i) => ({ id: i.id })),
        },
        tags: {
          create: data.tags?.create,
          set: offering.tags
            ?.filter((t) => !t.id.includes("-"))
            .map((t) => ({ id: t.id })),
        },
      },
      where: {
        id: offering.id,
      },
    };

    return updateVariables;
  } else {
    const createVariables: createMembershipOfferingVariables = {
      data: {
        ...data,
        addons: {
          connect: offering.addons.map((i) => ({ id: i.id })),
        },
        agreements: {
          connect: offering.agreements.map((i) => ({ id: i.id })),
        },
        charges: {
          create: offering.charges.map((i) => {
            return {
              amount: i.amount,
              billingCategory: {
                connect: {
                  id: i.billingCategory?.id,
                },
              },
              description: i.description,
            };
          }),
        },
        communications: {
          connect: offering.communications.map((i) => ({ id: i.id })),
        },
        coupons: {
          connect: offering.coupons.map((i) => ({ id: i.id })),
        },
        credits: {
          create: offering.credits.map((i) => {
            return {
              amount: i.amount,
              issuedAmount: i.amount,
              billingCategory: {
                connect: {
                  id: i.billingCategory.id,
                },
              },
              description: i.description,
            };
          }),
        },
        fees: {
          connect: offering.fees.map((f) => ({ id: f.id })),
        },
        priceLevels: {
          create: offering.priceLevels.map((i) => {
            return {
              price: i.price,
              number: i.number,
            };
          }),
        },
        studentInfoRequests: {
          connect: offering.studentInfoRequests.map((i) => ({ id: i.id })),
        },
        tags: {
          create: data.tags?.create,
          connect: offering.tags
            ?.filter((t) => !t.id.includes("-"))
            .map((t) => ({ id: t.id })),
        },
      },
    };

    return createVariables;
  }
}
